import { type IntlShape, createIntl } from "react-intl"

import type {
  GetInterBuExcelExportData200,
  InterBuExcelExportRow,
} from "@/networking/api/sharedData/types"
import { englishMessages } from "@/translations/en/englishMessages"
import { frenchMessages } from "@/translations/fr/frenchMessages"
import { isBlank, isNullOrUndefined } from "@/utils/ts-utils"

export type ExportLocale = "fr" | "en"

const intlByLocale: Record<ExportLocale, IntlShape> = {
  fr: createIntl({ locale: "fr", messages: frenchMessages }),
  en: createIntl({ locale: "en", messages: englishMessages }),
}

const directionFields: (keyof InterBuExcelExportRow)[] = ["direction"]

const motivationFields: (keyof InterBuExcelExportRow)[] = ["subject", "subjectAnswer", "subjectEnd"]

const dateFields: (keyof InterBuExcelExportRow)[] = [
  "createdAt",
  "updatedAt",
  "acceptedAt",
  "deniedAt",
  "revokedAt",
  "terminatedAt",
]

const columnKeys: (keyof InterBuExcelExportRow)[] = [
  "reference",
  "sharedDataId",
  "interBuId",
  "statusCode",
  "statusLabel",
  "direction",
  "createdAt",
  "updatedAt",
  "acceptedAt",
  "deniedAt",
  "revokedAt",
  "terminatedAt",
  "fromClusterId",
  "fromClusterName",
  "toClusterId",
  "toClusterName",
  "fromManagerId",
  "fromManagerFirstname",
  "fromManagerLastname",
  "toManagerId",
  "toManagerFirstname",
  "toManagerLastname",
  "fromUserId",
  "subject",
  "message",
  "subjectAnswer",
  "messageAnswer",
  "peopleId",
  "peopleFirstname",
  "peopleLastname",
  "peopleComments",
  "projectComments",
  "typeCode",
  "typeLabel",
  "subtypeCode",
  "subtypeLabel",
  "propertyTypeCode",
  "propertyTypeLabel",
  "projectMaturityCode",
  "projectMaturityLabel",
  "priceMin",
  "priceMax",
  "priceUnitCode",
  "priceUnitLabel",
  "surfaceMin",
  "surfaceMax",
  "surfaceUnitCode",
  "surfaceUnitLabel",
  "address1",
  "address2",
  "address3",
  "zipcode",
  "city",
  "country",
  "subjectEnd",
  "messageEnd",
  "attachmentName",
  "attachmentUrl",
  "targetClusterNames",
  "commentsJson",
]

/**
 * Builds one Excel sheet's rows for a given export locale (fr/en), independently from the
 * app's current display language: headers and frontend-owned labels (direction, motivation)
 * are translated via a dedicated IntlShape per locale.
 */
export const buildInterBuExcelRows = (
  payload: GetInterBuExcelExportData200,
  locale: ExportLocale
) => {
  const intl = intlByLocale[locale]

  const formatMessage = (id: string) => (isBlank(id) ? "" : intl.formatMessage({ id }))

  const formatDate = (value: string) => new Date(value).toLocaleDateString(locale)

  const formatDirection = (code: string | null | undefined) => {
    if (isBlank(code)) return ""
    const id = `userSharedData.interbu.export.direction.${code}`
    const label = intl.formatMessage({ id, defaultMessage: "" })
    return label !== "" ? label : code
  }

  const formatMotivation = (code: string | null | undefined) => {
    if (isBlank(code)) return ""
    const id = `userSharedData.interbu.motivation.${code}`
    const label = intl.formatMessage({ id, defaultMessage: "" })
    return label !== "" ? label : code
  }

  return payload["hydra:member"].map(item => {
    const row = {} as Record<string, string | number | undefined | null>

    for (const key of columnKeys) {
      const header = formatMessage(`userSharedData.interbu.export.column.${key}`)
      const value = item[key]

      if (dateFields.includes(key)) {
        row[header] = isNullOrUndefined(value) ? "" : formatDate(value as string)
        continue
      }

      if (motivationFields.includes(key)) {
        row[header] = formatMotivation(value as string | null | undefined)
        continue
      }

      if (directionFields.includes(key)) {
        row[header] = formatDirection(value as string | null | undefined)
        continue
      }

      if (typeof value === "number") {
        row[header] = value
        continue
      }

      row[header] = isBlank(value as string) ? "" : (value as string)
    }

    return row
  })
}
